import React, { useCallback } from 'react';
import { Info } from 'lucide-react';
import SectionShell from './SectionShell';

const TEXT_FIELDS = [
  { key: 'job_title',       label: 'Job title',          placeholder: 'e.g. Replace 15kV switchgear panel B' },
  { key: 'work_order',      label: 'Work order #',       placeholder: 'e.g. WO-12345' },
  { key: 'work_location',   label: 'Location',           placeholder: 'e.g. Substation 42, bay 3' },
  { key: 'crew_leader',     label: 'Crew leader',        placeholder: 'Name' },
  { key: 'date',            label: 'Date',               placeholder: 'YYYY-MM-DD', type: 'date' },
  { key: 'expected_hours',  label: 'Expected duration',  placeholder: 'e.g. 6 hours' },
];

const CONDITIONAL_FIELDS = [
  { key: 'voltage',              label: 'Voltage present',              type: 'text',     placeholder: 'e.g. 15 kV / 480 V / N-A' },
  { key: 'equipment_status',     label: 'Equipment status',             type: 'select',   options: ['de-energized', 'energized', 'unknown'] },
  { key: 'switching_required',   label: 'Switching required',           type: 'checkbox' },
  { key: 'clearance_required',   label: 'Clearance / hold-off required',type: 'checkbox' },
  { key: 'grounding_required',   label: 'Grounding required',           type: 'checkbox' },
  { key: 'lifting_required',     label: 'Lifting / rigging required',   type: 'checkbox' },
  { key: 'contractors_present',  label: 'Contractors on site',          type: 'checkbox' },
];

/**
 * JobInformationSection — the 6 required fields + 7 conditional flags used
 * by `computeJobRisk` (jobBriefRules.js). Editing any conditional flag
 * automatically re-scores Job Risk via the Editor's readiness memo.
 */
export default function JobInformationSection({ draft, onChange }) {
  const ji = draft.job_information || {};

  const set = useCallback((key, value) => {
    onChange({ job_information: { ...ji, [key]: value } });
  }, [ji, onChange]);

  return (
    <SectionShell
      icon={Info}
      title="Job Information"
      subtitle="Fill the required fields, then flag any high-risk conditions below to feed the Readiness scores."
      badge="Required"
      color="#facc15"
      testId="jbv2-section-job-info"
    >
      <div className="jbv2-field-grid">
        {TEXT_FIELDS.map((f) => (
          <label key={f.key} className={`jbv2-field${f.key === 'work_location' ? ' jbv2-field--wide' : ''}`}>
            <span className="jbv2-field-label">{f.label}</span>
            <input
              type={f.type || 'text'}
              className="jbv2-field-input"
              value={ji[f.key] || ''}
              placeholder={f.placeholder}
              onChange={(e) => set(f.key, e.target.value)}
              data-testid={`jbv2-jobinfo-${f.key}`}
            />
          </label>
        ))}
      </div>

      <div className="jbv2-field-label" style={{ marginTop: 4 }}>
        Conditions on this job
      </div>
      <div className="jbv2-field-grid">
        {CONDITIONAL_FIELDS.map((f) => {
          if (f.type === 'checkbox') {
            const on = !!ji[f.key];
            return (
              <label
                key={f.key}
                className={`jbv2-field-check${on ? ' jbv2-field-check--on' : ''}`}
                data-testid={`jbv2-jobinfo-${f.key}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => set(f.key, e.target.checked)}
                  data-testid={`jbv2-jobinfo-${f.key}-input`}
                />
                <span>{f.label}</span>
              </label>
            );
          }
          if (f.type === 'select') {
            return (
              <label key={f.key} className="jbv2-field">
                <span className="jbv2-field-label">{f.label}</span>
                <select
                  className="jbv2-field-select"
                  value={ji[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`jbv2-jobinfo-${f.key}`}
                >
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            );
          }
          return (
            <label key={f.key} className="jbv2-field">
              <span className="jbv2-field-label">{f.label}</span>
              <input
                type="text"
                className="jbv2-field-input"
                value={ji[f.key] || ''}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                data-testid={`jbv2-jobinfo-${f.key}`}
              />
            </label>
          );
        })}
      </div>
    </SectionShell>
  );
}
